import React, {
    useEffect, useRef, useState, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { closeWorkspaceOverlay } from 'common/store/workspaces/actions';
import { Loader } from 'semantic-ui-react';
import CloseIcon from '@mui/icons-material/Close';
import styles from './RubricsView.scss';

const getApp = () => (window as any).App;
const getRubricsIframeUrl = (enableAiAssistedMarking?: boolean) => {
    const app = getApp();
    const configuredUrl = app?.config?.rubricsTemplatesUrl;
    const mode = 'addToQuestionMode';

    const updateQueryParams = (query: string, ensureMode: boolean) => {
        const params = query ? query.split('&').filter(Boolean) : [];
        let replacedMode = false;
        let replacedAiFlag = false;

        const updatedParams = params.map((param) => {
            const [key] = param.split('=');
            if (key === 'mode') {
                replacedMode = true;
                return `mode=${mode}`;
            }
            if (key === 'enableAiAssistedMarking') {
                replacedAiFlag = true;
                return enableAiAssistedMarking ? 'enableAiAssistedMarking=true' : param;
            }
            return param;
        });

        if (!replacedMode && ensureMode) {
            updatedParams.push(`mode=${mode}`);
        }
        if (enableAiAssistedMarking && !replacedAiFlag) {
            updatedParams.push('enableAiAssistedMarking=true');
        }

        return updatedParams.join('&');
    };

    const updateSegment = (segment: string, ensureMode: boolean) => {
        const questionIndex = segment.indexOf('?');
        const path = questionIndex >= 0 ? segment.slice(0, questionIndex) : segment;
        const query = questionIndex >= 0 ? segment.slice(questionIndex + 1) : '';
        const updatedQuery = updateQueryParams(query, ensureMode);

        return updatedQuery ? `${path}?${updatedQuery}` : path;
    };

    const ensureModeParam = (rawUrl: string) => {
        const hashIndex = rawUrl.indexOf('#');
        const hasHash = hashIndex >= 0;
        const base = hasHash ? rawUrl.slice(0, hashIndex) : rawUrl;
        const fragment = hasHash ? rawUrl.slice(hashIndex + 1) : '';
        const baseHasMode = base.includes('mode=');

        let updatedBase: string;
        if (hasHash) {
            updatedBase = baseHasMode ? updateSegment(base, false) : base;
        } else {
            updatedBase = updateSegment(base, true);
        }

        const updatedFragment = hasHash && fragment.length > 0
            ? updateSegment(fragment, true)
            : fragment;

        return hasHash ? `${updatedBase}#${updatedFragment}` : updatedBase;
    };

    if (!configuredUrl) {
        return ensureModeParam(`${window.location.origin}/admin#rubrics/templatesModal`);
    }

    if (configuredUrl.includes('#rubrics') && !configuredUrl.includes('/templatesModal')) {
        const urlWithTemplatesModal = configuredUrl.replace(/#rubrics([^?]*)/, '#rubrics$1/templatesModal');
        return ensureModeParam(urlWithTemplatesModal);
    }

    return ensureModeParam(configuredUrl);
};

interface RubricsTemplatesListModalProps {
    enableAiAssistedMarking?: boolean;
}

const RubricsTemplatesListModal: React.FC<RubricsTemplatesListModalProps> = ({
    enableAiAssistedMarking,
}) => {
    const dispatch = useDispatch();
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const setIframeItemBankModalOpen = useCallback((isOpen: boolean) => {
        try {
            const iframeWindow = iframeRef.current?.contentWindow as any;
            if (iframeWindow?.App) {
                iframeWindow.App.isItemBankModalOpen = isOpen;
            }
        } catch (e) {
            console.error('cross origin error:', e);
        }
    }, []);

    const handleClose = useCallback(() => {
        const app = getApp();
        if (app) app.isItemBankModalOpen = false;
        setIframeItemBankModalOpen(false);
        dispatch(closeWorkspaceOverlay());
    }, [dispatch, setIframeItemBankModalOpen]);

    const handleIframeLoaded = useCallback(() => {
        setIframeItemBankModalOpen(true);
        setIsIframeLoaded(true);
    }, [setIframeItemBankModalOpen]);

    useEffect(() => {
        return () => {
            try {
                setIframeItemBankModalOpen(false);
            } catch (e) {
                console.error('Error during RubricsTemplatesListModal cleanup:', e);
            }
        };
    }, [setIframeItemBankModalOpen]);

    return (
        <>
            {!isIframeLoaded && <Loader />}
            <div className={styles.rubricsTemplatesListModalView}>
                <div className={styles.rubricsTemplatesListModalContent}>
                    <div className={styles.modalHeaderActions || 'modal-header-actions'}>
                        <button
                            type="button"
                            className={`${styles.close} ${styles.updated}`}
                            onClick={handleClose}
                        >
                            <CloseIcon sx={{ fontSize: 'inherit', color: 'inherit' }} />
                        </button>
                    </div>

                    <iframe
                        ref={iframeRef}
                        src={getRubricsIframeUrl(enableAiAssistedMarking)}
                        title="Rubrics templates"
                        className={styles.rubricsIframe}
                        onLoad={handleIframeLoaded}
                    />

                    <div className={styles.modalFooterActions}>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnLink} ${styles.closeNow}`}
                            onClick={handleClose}
                        >
                            {getApp()?.i18n?.t?.('general.cancel') || 'Cancel'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RubricsTemplatesListModal;
